import type { IScope } from "angular";
import { finalize } from "rxjs/operators";

import type { StateService } from "@uirouter/angularjs";

import type { IResponseWithLoading, ISite } from "App/Modules/Models";
import { SiteFunctions, SiteState } from "App/Modules/Models";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IPageService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

export default class SiteActivationComponentController {
    public static $inject: string[] = ["$scope", "mmSitesRepository", "irPageService", "$state"];

    public siteId: string;

    public site: IResponseWithLoading<ISite>;

    public siteStateColour: string;

    public siteUrl: string;

    public showActivateButton = false;

    public showReactivateButton = false;

    public showDeactivateButton = false;

    public otherModesCollapsed = true;

    public normalModeResponse: IResponseWithLoading<ISite>;

    public maintenanceModeResponse: IResponseWithLoading<ISite>;

    public endedModeResponse: IResponseWithLoading<ISite>;

    constructor(
        private readonly $scope: IScope,
        private readonly sitesRepository: ISitesRepository,
        private readonly pageService: IPageService,
        private readonly stateService: StateService,
    ) {}

    public $onInit() {
        extendWithLoadingAndFail(this.sitesRepository.getSite(this.siteId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    this.site = response;
                    if (!response.isLoading && response.data) {
                        this.siteStateColour = SiteFunctions.getSiteStateColour(response.data.siteState);
                        this.setButtonVisiblity();
                        this.siteUrl = this.stateService.href(this.stateService.$current, this.stateService.params, {
                            absolute: true,
                        });
                    }
                    this.$scope.$applyAsync();
                },
                (error) => {
                    this.site = error;
                },
            );
    }

    public activateSite() {
        const siteState = SiteState[SiteState.Normal as any as keyof typeof SiteState];
        this.normalModeResponse = { isLoading: true };
        extendWithLoadingAndFail(this.sitesRepository.patchSite(this.siteId, { siteState }))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (patchResponse) => {
                    if (!patchResponse.isLoading && patchResponse.data) {
                        this.normalModeResponse = patchResponse;

                        this.updateLabelsAndButtons(patchResponse);
                    }
                    this.$scope.$applyAsync();
                },
                (error) => {
                    this.normalModeResponse = error;
                },
            );
    }

    public reactivateSite() {
        const siteState = SiteState[SiteState.Maintenance as any as keyof typeof SiteState];
        this.maintenanceModeResponse = { isLoading: true };
        extendWithLoadingAndFail(this.sitesRepository.patchSite(this.siteId, { siteState }))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (patchResponse) => {
                    if (!patchResponse.isLoading && patchResponse.data) {
                        this.maintenanceModeResponse = patchResponse;

                        this.updateLabelsAndButtons(patchResponse);
                    }
                    this.$scope.$applyAsync();
                },
                (error) => {
                    this.maintenanceModeResponse = error;
                },
            );
    }

    public deactivateSite() {
        const siteState = SiteState[SiteState.Ended as any as keyof typeof SiteState];
        this.endedModeResponse = { isLoading: true };
        extendWithLoadingAndFail(this.sitesRepository.patchSite(this.siteId, { siteState }))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (patchResponse) => {
                    if (!patchResponse.isLoading && patchResponse.data) {
                        this.endedModeResponse = patchResponse;

                        this.updateLabelsAndButtons(patchResponse);
                    }
                    this.$scope.$applyAsync();
                },
                (error) => {
                    this.endedModeResponse = error;
                },
            );
    }

    private setButtonVisiblity() {
        if (this.site.data.siteState === SiteState.NotStarted || this.site.data.siteState === SiteState.Maintenance) {
            this.showActivateButton = true;
            this.showDeactivateButton = false;
            this.showReactivateButton = false;
        }

        if (this.site.data.siteState === SiteState.Ended) {
            this.showReactivateButton = true;
            this.showActivateButton = false;
            this.showDeactivateButton = false;
        }

        if (this.site.data.siteState === SiteState.Normal) {
            this.showDeactivateButton = true;
            this.showActivateButton = false;
            this.showReactivateButton = false;
        }
    }

    private updateLabelsAndButtons(patchResponse: IResponseWithLoading<ISite>) {
        this.site.data.siteState = patchResponse.data.siteState;
        this.site.data.siteStateName = patchResponse.data.siteStateName;
        this.siteStateColour = SiteFunctions.getSiteStateColour(this.site.data.siteState);
        this.setButtonVisiblity();

        this.normalModeResponse = null;
        this.maintenanceModeResponse = null;
        this.endedModeResponse = null;
    }
}
